import React from 'react';
import { Activity, Info, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatNumber } from '../../../utils/analysis.utils';
import { Tooltip } from '../../../../../shared/components/Tooltip';

interface PeakShavingMaximumCardProps {
  analysis: any;
  exchangeRate: number;
  configuration?: any;
}

export const PeakShavingMaximumCard: React.FC<PeakShavingMaximumCardProps> = ({
  analysis,
  exchangeRate,
  configuration
}) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-black flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
              <Activity className="w-4 h-4 text-brand-primary" />
            </div>
            {t('optimizationAnalysis.theoretical.peakShavingCard.title')}
          </h3>
          <Tooltip
            position="bottom"
            content={
              <div className="text-xs text-gray-700 space-y-2">
                <p className="font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.peakShavingCard.tooltipTitle')}</p>
                <div className="space-y-2">
                  <div className="border-l-2 border-gray-400 pl-2">
                    <p className="font-semibold">{t('optimizationAnalysis.theoretical.peakShavingCard.hpTitle')}</p>
                    <ul className="ml-2 mt-1 space-y-0.5">
                      <li>{t('optimizationAnalysis.theoretical.peakShavingCard.hpPeakHours')}</li>
                      <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.peakShavingCard.hpWinter') }} />
                      <li dangerouslySetInnerHTML={{ __html: t('optimizationAnalysis.theoretical.peakShavingCard.hpSummer') }} />
                      <li>{t('optimizationAnalysis.theoretical.peakShavingCard.hpHigherRate')}</li>
                      <li className="text-brand-primary font-medium">{t('optimizationAnalysis.theoretical.peakShavingCard.hpCanEliminate')}</li>
                    </ul>
                  </div>
                  <div className="border-l-2 border-gray-400 pl-2">
                    <p className="font-semibold">{t('optimizationAnalysis.theoretical.peakShavingCard.hfpTitle')}</p>
                    <ul className="ml-2 mt-1 space-y-0.5">
                      <li>{t('optimizationAnalysis.theoretical.peakShavingCard.hfpAnyHour')}</li>
                      <li>{t('optimizationAnalysis.theoretical.peakShavingCard.hfpRollingAvg')}</li>
                      <li>{t('optimizationAnalysis.theoretical.peakShavingCard.hfpChargedMonthly')}</li>
                      <li className="text-gray-600 font-medium">{t('optimizationAnalysis.theoretical.peakShavingCard.hfpCannotEliminate')}</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded">
                  <p className="text-xs font-semibold text-gray-800 mb-2">{t('optimizationAnalysis.theoretical.peakShavingCard.seasonalPatternTitle')}</p>
                  <div className="grid grid-cols-12 gap-0.5">
                    {['J','F','M','A','M','J','J','A','S','O','N','D'].map((month, idx) => (
                      <div key={month} className={`text-center p-0.5 text-xs font-bold rounded ${
                        idx >= 3 && idx <= 8
                          ? 'bg-gray-200 text-gray-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {month}
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 text-xs">
                    <span className="text-gray-700">{t('optimizationAnalysis.theoretical.peakShavingCard.winterCurrent')}</span><br/>
                    <span className="text-gray-600">{t('optimizationAnalysis.theoretical.peakShavingCard.summerCarryover')}</span>
                  </div>
                </div>
                <p className="mt-2 font-medium bg-gray-100 p-2 rounded text-gray-800">
                  {t('optimizationAnalysis.theoretical.peakShavingCard.carryoverNote')}
                </p>
              </div>
            }
          >
            <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
              <Info className="w-4 h-4 text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(
                analysis.peakShaving.annualSavingsUsd +
                (analysis.generatorSavings?.enabled ? analysis.generatorSavings.annualCost : 0)
              )}
            </p>
            {analysis.generatorSavings?.enabled ? (
              <div className="text-sm text-gray-600 mt-1">
                <p>{t('optimizationAnalysis.theoretical.peakShavingCard.totalAnnualSavings')}</p>
                <p className="text-xs mt-1">
                  ({formatCurrency(analysis.peakShaving.annualSavingsUsd)} {t('optimizationAnalysis.theoretical.peakShavingCard.demand')} + {formatCurrency(analysis.generatorSavings.annualCost)} {t('optimizationAnalysis.theoretical.peakShavingCard.generator')})
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                {t('optimizationAnalysis.theoretical.peakShavingCard.annualDemandSavings')}
              </p>
            )}
            <p className="text-lg font-semibold text-gray-900 mt-2">
              {formatCurrency(
                analysis.peakShaving.monthlySavingsUsd +
                (analysis.generatorSavings?.enabled ? analysis.generatorSavings.monthlyCost : 0)
              )}{t('optimizationAnalysis.theoretical.peakShavingCard.monthlySavings')}
            </p>
            <p className="text-sm text-gray-500 mt-2">{t('optimizationAnalysis.theoretical.peakShavingCard.byEliminating')}</p>

            <div className="mt-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-gray-500 text-xs mb-1">{t('optimizationAnalysis.theoretical.peakShavingCard.currentHpDemand')}</p>
                    <p className="font-bold text-gray-900 text-2xl">{formatNumber(analysis.peakShaving.demandHpKw)} kW</p>
                    <p className="text-xs text-gray-600">{formatCurrency(analysis.peakShaving.demandHpRate / exchangeRate)}/kW</p>
                    <p className="text-xs text-gray-600 mt-1">18:00-22:00 {t('optimizationAnalysis.theoretical.peakShavingCard.weekdays')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500 text-xs mb-1">{t('optimizationAnalysis.theoretical.peakShavingCard.withBESS')}</p>
                    <p className="font-bold text-brand-primary text-2xl">0 kW</p>
                    <p className="text-xs text-brand-primary">{t('optimizationAnalysis.theoretical.peakShavingCard.fullyEliminated')}</p>
                    <p className="text-xs text-brand-primary mt-1">{t('optimizationAnalysis.theoretical.peakShavingCard.bessDischarges')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="bg-gray-50 p-2 rounded">
              <div className="flex justify-between items-start mb-1">
                <span className="text-gray-700 font-medium">{t('optimizationAnalysis.theoretical.peakShavingCard.horaPuntaHP')}</span>
                <span className="text-xs text-gray-500">18:00-22:00 {t('optimizationAnalysis.theoretical.peakShavingCard.weekdays')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{formatNumber(analysis.peakShaving.demandHpKw)} kW × {formatCurrency(analysis.peakShaving.demandHpRate / exchangeRate)}/kW</span>
                <span className="font-bold text-gray-900">
                  {formatCurrency(analysis.peakShaving.demandHpCharge / exchangeRate)}/mo
                </span>
              </div>
              <div className="text-xs text-gray-700 mt-1">
                <div>{t('optimizationAnalysis.theoretical.peakShavingCard.winterAprSep')}</div>
                <div>{t('optimizationAnalysis.theoretical.peakShavingCard.summerOctMar')}</div>
                <div className="text-brand-primary font-medium">{t('optimizationAnalysis.theoretical.peakShavingCard.canBeEliminated')}</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-gray-900 mb-2">{t('optimizationAnalysis.theoretical.peakShavingCard.combinedPotential')}</p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">{t('optimizationAnalysis.theoretical.peakShavingCard.energyArbitrage')}</span>
                <span className="font-medium">{formatCurrency(analysis.theoretical.maximum)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">{t('optimizationAnalysis.theoretical.peakShavingCard.peakShaving')}</span>
                <span className="font-medium">{formatCurrency(analysis.peakShaving.annualSavingsUsd)}</span>
              </div>
              {analysis.generatorSavings?.enabled && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('optimizationAnalysis.theoretical.peakShavingCard.generatorReplacement')}</span>
                  <span className="font-medium">{formatCurrency(analysis.generatorSavings.annualCost)}</span>
                </div>
              )}
              <div className="pt-1 mt-1 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.peakShavingCard.total')}</span>
                  <span className="text-lg font-bold text-brand-primary">
                    {formatCurrency(
                      analysis.peakShaving.combinedSavings +
                      (analysis.generatorSavings?.annualCost || 0)
                    )}
                  </span>
                </div>
                <p className="text-xs text-gray-700 mt-1 text-right">
                  {formatNumber(
                    ((analysis.peakShaving.combinedSavings + (analysis.generatorSavings?.annualCost || 0)) /
                    (analysis.totalAnnualEnergyCharge + analysis.peakShaving.annualSavingsUsd + (analysis.generatorSavings?.annualCost || 0))) * 100,
                    1
                  )}% {t('optimizationAnalysis.theoretical.peakShavingCard.totalReduction')}
                </p>
              </div>
            </div>
          </div>

          {analysis.generatorSavings?.enabled && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-gray-600" />
                <p className="text-xs font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.peakShavingCard.generatorSavingsTitle')}</p>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('optimizationAnalysis.theoretical.peakShavingCard.operatingCost')}</span>
                  <span className="font-medium">{formatCurrency(analysis.generatorSavings.costPerHour)}/hour</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('optimizationAnalysis.theoretical.peakShavingCard.dailyUsage')}</span>
                  <span className="font-medium">{analysis.generatorSavings.hoursPerDay} hours × {formatCurrency(analysis.generatorSavings.costPerHour)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('optimizationAnalysis.theoretical.peakShavingCard.dailyCost')}</span>
                  <span className="font-medium">{formatCurrency(analysis.generatorSavings.dailyCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('optimizationAnalysis.theoretical.peakShavingCard.weekdaysPerYear')}</span>
                  <span className="font-medium">{analysis.generatorSavings.weekdaysPerYear} days</span>
                </div>
                <div className="pt-1 mt-1 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-gray-700">{t('optimizationAnalysis.theoretical.peakShavingCard.monthlySavingsLabel')}</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(analysis.generatorSavings.monthlyCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">{t('optimizationAnalysis.theoretical.peakShavingCard.annualSavings')}</span>
                    <span className="font-bold text-gray-900">{formatCurrency(analysis.generatorSavings.annualCost)}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-700 mt-2 italic">
                {t('optimizationAnalysis.theoretical.peakShavingCard.bessEliminatesNote')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};